/**
 * OnboardingShell — Minimal layout for employees in 'onboarding' status.
 *
 * Shows:
 * - Top bar with hiring authority logo/name (group or subsidiary) + subsidiary name
 * - "Onboarding" badge
 * - User name + avatar + logout
 * - The onboarding portal content only
 *
 * Logo logic:
 * - If hiring_authority === 'group' → show GROUP logo + group name as primary, subsidiary name secondary
 * - If hiring_authority === 'subsidiary' → show SUBSIDIARY logo + name as primary (no group shown)
 */

import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  CheckCircleOutline as TasksIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { TYPE } from '@/theme/typography';
import { DEFAULT_PALETTE } from '@/config/colorPalettes';
import { resolveUploadUrl } from '@/utils/format';

export default function OnboardingShell() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const logout = useAuthStore((s) => s.logout);

  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  const companyColour = activeCompany?.company_colour || DEFAULT_PALETTE.primary;
  const companyName = activeCompany?.company_name || '';
  const companyLogo = resolveUploadUrl(activeCompany?.company_logo_url);
  const groupName = activeCompany?.group_name;
  const groupLogo = resolveUploadUrl(activeCompany?.group_logo_url);
  const hiringAuthority = activeCompany?.hiring_authority || 'group';

  // Determine branding based on hiring authority
  const isGroupHiring = hiringAuthority === 'group' && !!groupName;
  const primaryName = isGroupHiring ? groupName : companyName;
  const primaryLogo = isGroupHiring ? groupLogo : companyLogo;
  const secondaryName = isGroupHiring ? companyName : null;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore — clear state regardless
    }
    logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Company colour bar */}
      <Box sx={{ height: 4, bgcolor: companyColour }} />

      {/* Top bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
          {/* Left: Logo + names + onboarding badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            {/* Hiring authority logo */}
            {primaryLogo ? (
              <Box
                component="img"
                src={primaryLogo}
                alt={primaryName}
                sx={{
                  height: 40,
                  maxWidth: 120,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: companyColour,
                  fontSize: TYPE.base,
                  fontWeight: TYPE.bold,
                  flexShrink: 0,
                }}
              >
                {(primaryName || '?')[0]}
              </Avatar>
            )}

            {/* Names */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: TYPE.bold,
                  color: companyColour,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {primaryName}
              </Typography>
              {secondaryName && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.2,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {secondaryName}
                </Typography>
              )}
            </Box>

            {/* Vertical divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />

            {/* Onboarding badge */}
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: TYPE.xs,
                fontWeight: TYPE.semibold,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Onboarding
            </Box>
          </Box>

          {/* Right: User avatar + logout */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.primary', fontWeight: TYPE.medium, display: { xs: 'none', sm: 'block' } }}
            >
              {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="Open user menu">
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: companyColour,
                  fontSize: TYPE.sm,
                  fontWeight: TYPE.bold,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Page navigation tabs */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
          <Tabs
            value="/my-onboarding"
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.base },
            }}
          >
            <Tab icon={<TasksIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="My Onboarding" value="/my-onboarding" />
          </Tabs>
        </Box>
      </Box>

      {/* Main content — centered, clean */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          overflow: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1100, p: { xs: 2, sm: 3, md: 4 }, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          textAlign: 'center',
          py: 1.5,
          fontSize: TYPE.xs,
          color: 'text.secondary',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        &copy; {new Date().getFullYear()} {groupName || companyName}
      </Box>
    </Box>
  );
}
